import React, { useEffect, useState, useRef } from 'react'
import {
  List,
  Datagrid,
  TextField,
  DateField,
  useTranslate,
  useListContext,
  FunctionField
} from 'react-admin'
import { Container } from '@mui/material'
import { CardCommon } from 'src/components/uiElements'
import { FormattedTextFieldWithStatus } from '../../sales/reservation/helper/FormattedTextFieldWithStatus'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { PostFilterButtonOperation, PostFilterFormOperation } from './OperationFilter'
import { Paginator } from './OperationPaginator'
import IconButton from '@mui/material/IconButton'
import LinkIcon from '@mui/icons-material/Link';
import Tooltip from '@mui/material/Tooltip'
import { DisableOperationButton } from './DeleteOperationButton'
import { OperationExportButton } from './helper/OperationExportButton'

const CustomReservationButton = ({ reservationId }) => {
  const translate = useTranslate()

  const handleClick = () => {
    window.open(`/#/reservation/${reservationId}/show`, '_blank')
  };

  return (
    <Tooltip
      title={translate('ra.action.see_something', { thing: translate('ra.entity.reservation') })}
      placement='top'
    >
      <IconButton size='small' onClick={handleClick} aria-label='view reservation'>
        <LinkIcon fontSize='small' />
      </IconButton>
    </Tooltip>
  )
};

const OperationList = (props) => {
  const translate = useTranslate()
  const { isFetching, page, filterValues } = useListContext()
  const prevPageRef = useRef()
  const prevFiltersRef = useRef(filterValues)
  const [lastIdsStack, setLastIdsStack] = useState([]) // Historial de last_id
  const [currentPage, setCurrentPage] = useState(1) // Página actual

  const [pageChanged, setPageChanged] = useState(false)

  useEffect(() => {
    // Comprobar si la página ha cambiado y si isFetching es true
    if (prevPageRef.current !== page && isFetching) {
      prevPageRef.current = page
      setPageChanged(true)
    }
    if (prevFiltersRef.current !== filterValues && isFetching) {
      prevFiltersRef.current = filterValues
      setPageChanged(true)
    } else if (!isFetching) {
      setPageChanged(false)
    }
  }, [isFetching, page, filterValues])

  const allFilters = [
    { source: 'created_at', name: 'ra.field.created_at' },
    { source: 'external_id', name: 'ra.field.operation_id' },
    { source: 'general_status', name: 'ra.field.general_state' },
    { source: 'flow_status', name: 'ra.field.flow_status' },
    { source: 'work_unit_token', name: 'ra.field.work_unit' },
    { source: 'airline', name: 'ra.field.airline' },
    { source: 'destination_type', name: 'ra.field.destination_type' },
    { source: 'currency', name: 'ra.field.currency' },
    { source: 'payment_method', name: 'ra.field.payment_method_filter' },
    { source: 'checkin', name: 'ra.field.checkin' },
    { source: 'has_flexible_rate', name: 'ra.field.flexi' },
    { source: 'has_ancillary_baggage', name: 'ra.field.extra_baggage' },
    { source: 'has_ancillary_hepstar', name: 'ra.field.hepstar' },
    { source: 'has_service_package', name: 'ra.title.servicePackage' },
    { source: 'promotion_label', name: 'ra.field.promotion' },
    { source: 'booking_number', name: 'ra.field.provider_code' },
    { source: 'source', name: 'ra.field.source' },
    // { source: 'destination', name: 'ra.field.destination' },
  ]

  const reservationNumber = (record) => {
    const reservation_number = record?.reservation_number
    if (reservation_number){
      return reservation_number
    }
    return '-'
  }

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.operation')}>
        <List
          {...props}
          exporter={false}
          component='div'
          sort={{ field: 'created_at', order: 'DESC' }}
          pagintor={false}
          // Para la primera página no enviamos last_id; para las siguientes usamos el cursor guardado
          filter={
            currentPage > 1 && lastIdsStack[currentPage - 2]
              ? { last_id: lastIdsStack[currentPage - 2] }
              : {}
          }
          pagination={
            <Paginator
              lastIdsStack={lastIdsStack}
              setLastIdsStack={setLastIdsStack}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          }
          actions={
            <ListViewTopToolbar
              customFilter
              listFilters={allFilters}
              CustomExportButton={OperationExportButton }
              exporterDisable={true}
              FilterButtonComponent={PostFilterButtonOperation}
              FilterFormComponent={(props) => (
                <PostFilterFormOperation 
                  {...props} 
                  onFilterApply={() => {
                    setCurrentPage(1)
                    setLastIdsStack([])
                  }}
                />
              )}
            />
          }
        >
          <Datagrid isLoading={pageChanged}>
            <DateField source='created_at' label={translate('ra.field.pickup_date')} showTime />
            <TextField source='external_id' label={translate('ra.field.operation_id')} />
            <TextField source='work_unit.long_name' label={translate('ra.field.work_unit')} />
            <FormattedTextFieldWithStatus
              label={translate('ra.field.general_state')}
              source='general_status'
            />
            <FormattedTextFieldWithStatus label={translate('ra.field.flow_status')} source='flow_status' />
            <FunctionField
              label={translate('ra.entity.reservation')}
              render={(record) => reservationNumber(record)}
            />
            <ListViewActionsButtons
              label={translate('ra.action.actions')}
              fieldsName={['short_name']}
              genderElement='femenine'
              disableEdit={true}
              disableRevision={true}
              CustomButton={CustomReservationButton}
              DeleteButtonComponent={DisableOperationButton}
            />
          </Datagrid>
        </List>
      </CardCommon>
    </Container>
  )
}
export default OperationList